import { useState, useRef, useCallback, SyntheticEvent } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Tabs,
  Tab,
  Button,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HomeIcon from '@mui/icons-material/Home';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import VerifiedIcon from '@mui/icons-material/Verified';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SaveIcon from '@mui/icons-material/Save';
import { useLanguage } from '../../../../context/LanguageContext';
import { useLayoutPreferences } from '../../../../context/LayoutPreferencesContext';
import CommunicationDefinitionStep, { ChannelType, DefinitionFormData } from './CommunicationDefinitionStep';
import CommunicationTargetingStep, { TargetingFormData } from './CommunicationTargetingStep';
import CommunicationCreationStep, { CreationStepHandle } from './CommunicationCreationStep';
import CommunicationOffersStep, { OffersStepHandle, OfferFormData, emptyOffer, offerInputToForm } from './CommunicationOffersStep';
import CommunicationValidationStep from './CommunicationValidationStep';
import DiscardChangesDialog from './DiscardChangesDialog';
import { useCreateCommunicationInput, useCreateOfferInput, useUpdateCommunicationInput } from '../../../../hooks/Marketing';
import { CommunicationInputData } from '../../../../hooks/Marketing/useGetLatestAnimations';

const TRIGGER_ID_MAP: Record<string, number> = {
  montant: 1, article: 2, points: 3, passage: 4, action: 5, inscription: 6,
};

const PREREQ_ID_MAP: Record<string, number> = {
  montant: 1, article: 2, points: 3,
};

const CHANNEL_ID: Record<ChannelType, number> = {
  email: 2, sms: 3, tikeo: 4, cashdesk: 5, '': 0,
};

const CHANNEL_BY_ID: Record<number, ChannelType> = {
  2: 'email', 3: 'sms', 4: 'tikeo', 5: 'cashdesk',
};

interface CommunicationDialogProps {
  open: boolean;
  onClose: () => void;
  animationInputId: number | null;
  onEnsureAnimationId?: () => Promise<number | null>;
  onSaved?: () => void;
  editingComm?: CommunicationInputData | null;
}

const STEPS = [
  { label: 'Définition', Icon: HomeIcon },
  { label: 'Ciblage', Icon: TrackChangesIcon },
  { label: 'Création', Icon: BorderColorIcon },
  { label: 'Offre(s)', Icon: CardGiftcardIcon },
  { label: 'Validation', Icon: VerifiedIcon },
];

const INITIAL_DEFINITION: DefinitionFormData = {
  channel: '', title: '', description: '', sendDate: '', sendByCustomer: false,
};

const toDatetimeLocal = (s: string | null | undefined): string => {
  if (!s) return '';
  if (s.includes('T')) return s.slice(0, 16);
  if (s.length >= 10) return s.slice(0, 10) + 'T00:00';
  return '';
};

const INITIAL_TARGETING: TargetingFormData = {
  title: '', description: '', queryId: null, queryDescription: '', nbRecipients: null,
};

function CommunicationDialog({
  open, onClose, animationInputId, onEnsureAnimationId, onSaved, editingComm,
}: CommunicationDialogProps) {
  const { t } = useLanguage();
  const { sidebarColor } = useLayoutPreferences();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  const [activeStep, setActiveStep] = useState(0);
  const [definition, setDefinition] = useState<DefinitionFormData>(() =>
    editingComm
      ? {
          channel: CHANNEL_BY_ID[editingComm.defChannelId] ?? '',
          title: editingComm.defTitle,
          description: editingComm.defDescription ?? '',
          sendDate: toDatetimeLocal(editingComm.defSendStart),
          sendByCustomer: editingComm.defChannelId === CHANNEL_ID['cashdesk'],
        }
      : INITIAL_DEFINITION
  );
  const [targeting, setTargeting] = useState<TargetingFormData>(() =>
    editingComm
      ? {
          title: editingComm.targetTitle ?? '',
          description: editingComm.targetDescription ?? '',
          queryId: editingComm.targetQueryId ?? null,
          queryDescription: (editingComm.creationPayload?._queryDesc as string) ?? '',
          nbRecipients: null,
        }
      : INITIAL_TARGETING
  );
  const [communicationInputId, setCommunicationInputId] = useState<number | null>(
    () => editingComm?.id ?? null
  );
  const [isDirty, setIsDirty] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftError, setDraftError] = useState('');
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  const [hasCreation, setHasCreation] = useState(false);
  const [batAcknowledged, setBatAcknowledged] = useState(false);
  const [validationOffers, setValidationOffers] = useState<OfferFormData[]>([]);
  const [mountedSteps, setMountedSteps] = useState<Set<number>>(() => new Set([0]));
  const [localOffers, setLocalOffers] = useState<OfferFormData[]>(() =>
    editingComm?.offerInputs && editingComm.offerInputs.length > 0
      ? editingComm.offerInputs.map(offerInputToForm)
      : [emptyOffer('1')]
  );
  const [usedOfferIndices, setUsedOfferIndices] = useState<Set<number>>(new Set());

  const creationPayloadRef = useRef<Record<string, unknown> | null>(
    editingComm?.creationPayload ?? null
  );

  const creatingRef = useRef(false);
  const offersRef = useRef<OffersStepHandle>(null);
  const creationStepRef = useRef<CreationStepHandle>(null);

  const { createCommunicationInput, isLoading: saving, error: saveError } = useCreateCommunicationInput();
  const { updateCommunicationInput } = useUpdateCommunicationInput();
  const { createOfferInput } = useCreateOfferInput();

  const handleDefinitionChange = useCallback((patch: Partial<DefinitionFormData>) => {
    setDefinition(prev => {
      if (patch.channel !== undefined && patch.channel !== prev.channel) {
        setLocalOffers([emptyOffer('1')]);
        setUsedOfferIndices(new Set());
        setMountedSteps(new Set([0]));
      }
      return { ...prev, ...patch };
    });
    setIsDirty(true);
  }, []);

  const handleTargetingChange = useCallback((patch: Partial<TargetingFormData>) => {
    setTargeting(prev => ({ ...prev, ...patch }));
    setIsDirty(true);
  }, []);

  const canSaveDefinition =
    definition.channel !== '' && definition.title.trim() !== '';

  const ensureCommunicationSaved = async (): Promise<number | null> => {
    if (communicationInputId !== null) return communicationInputId;
    if (creatingRef.current) return null;
    if (!canSaveDefinition) return null;

    const animId = animationInputId ?? await onEnsureAnimationId?.();
    if (animId === null || animId === undefined) return null;

    creatingRef.current = true;
    try {
      const result = await createCommunicationInput({
        animationInput: `/api/animation_inputs/${animId}`,
        defTitle: definition.title.trim(),
        defDescription: definition.description.trim() || undefined,
        defChannelId: CHANNEL_ID[definition.channel],
        defSendStart: definition.sendDate || undefined,
        defNotSendable: definition.sendByCustomer ? 1 : 0,
        targetTitle: targeting.title.trim() || undefined,
        targetDescription: targeting.description.trim() || undefined,
        targetQueryId: targeting.queryId ?? undefined,
        creationPayload: {
          ...(creationPayloadRef.current ?? {}),
          _queryDesc: targeting.queryDescription.trim() || undefined,
        },
      });
      if (result) {
        setCommunicationInputId(result.id);
        return result.id;
      }
      return null;
    } finally {
      creatingRef.current = false;
    }
  };

  const patchAllData = async (commId: number): Promise<void> => {
    await updateCommunicationInput(commId, {
      defTitle: definition.title.trim(),
      defDescription: definition.description.trim() || undefined,
      defSendStart: definition.sendDate || undefined,
      defNotSendable: definition.sendByCustomer ? 1 : 0,
      targetTitle: targeting.title.trim() || undefined,
      targetDescription: targeting.description.trim() || undefined,
      targetQueryId: targeting.queryId ?? undefined,
      creationPayload: {
        ...(creationPayloadRef.current ?? {}),
        _queryDesc: targeting.queryDescription.trim() || undefined,
      },
    });
  };

  const goToStep = (step: number) => {
    setMountedSteps(prev => { prev.add(step); return new Set(prev); });
    if (step === 4) setValidationOffers(localOffers);
    setActiveStep(step);
  };

  const handleTabChange = (_: SyntheticEvent, newStep: number) => {
    goToStep(newStep);
    setDraftError('');
  };

  const handleNext = () => {
    if (activeStep < STEPS.length - 1) {
      goToStep(activeStep + 1);
      setDraftError('');
    }
  };

  const hasUnsavedData =
    (communicationInputId === null && (definition.title.trim() !== '' || definition.channel !== '')) ||
    (communicationInputId !== null && isDirty);

  const resetDialog = () => {
    creatingRef.current = false;
    creationPayloadRef.current = null;
    setActiveStep(0);
    setDefinition(INITIAL_DEFINITION);
    setTargeting(INITIAL_TARGETING);
    setCommunicationInputId(null);
    setIsDirty(false);
    setDraftError('');
    setHasCreation(false);
    setBatAcknowledged(false);
    setValidationOffers([]);
    setLocalOffers([emptyOffer('1')]);
    setUsedOfferIndices(new Set());
    setMountedSteps(new Set([0]));
  };

  const handleClose = () => {
    if (hasUnsavedData) {
      setConfirmCloseOpen(true);
      return;
    }
    resetDialog();
    onClose();
  };

  const handleConfirmDiscard = () => {
    setConfirmCloseOpen(false);
    resetDialog();
    onClose();
  };

  const handleConfirmSaveDraft = async () => {
    setConfirmCloseOpen(false);
    await handleSaveDraft();
  };

  const saveOffersForComm = async (commId: number) => {
    const offers: OfferFormData[] = offersRef.current?.getOffers() ?? localOffers;
    const hasContent = (o: OfferFormData) =>
      o.title.trim() !== '' || o.description.trim() !== '' || o.offerNumber.trim() !== '';
    const toSave = offers.filter(hasContent);
    await Promise.all(
      toSave.map(o =>
        createOfferInput({
          communicationInput: `/api/communication_inputs/${commId}`,
          title: o.title.trim() || undefined,
          description: o.description.trim() || undefined,
          offerNumber: o.offerNumber.trim() || undefined,
          startValidityDate: o.startValidityDate || undefined,
          endValidityDate: o.endValidityDate || undefined,
          nbMaxEdit: o.nbMaxEdit ? Number(o.nbMaxEdit) : undefined,
          nbMaxUse: o.nbMaxUse ? Number(o.nbMaxUse) : undefined,
          refDiscountTypeId: o.discountTypeIds[0] ?? undefined,
          payloadRefDiscountType: o.discountTypeIds.length > 0 ? {
            remiseMode: o.remiseMode,
            remiseValue: o.remiseValue,
            pointsMode: o.pointsMode,
            pointsValue: o.pointsValue,
          } : undefined,
          refEditConditionId: o.triggerId ? TRIGGER_ID_MAP[o.triggerId] : undefined,
          payloadRefEditCondition: o.triggerId ? {
            triggerMontantMin: o.triggerMontantMin,
            triggerMontantMax: o.triggerMontantMax,
            triggerArticleMin: o.triggerArticleMin,
            triggerArticleMax: o.triggerArticleMax,
            triggerPointsMin: o.triggerPointsMin,
            triggerPointsMax: o.triggerPointsMax,
          } : undefined,
          refUseConditionId: o.prereqId ? PREREQ_ID_MAP[o.prereqId] : undefined,
          payloadRefUseCondition: o.prereqId ? {
            prereqMontantMin: o.prereqMontantMin,
            prereqMontantMax: o.prereqMontantMax,
            prereqArticleMin: o.prereqArticleMin,
            prereqArticleMax: o.prereqArticleMax,
            prereqPointsMin: o.prereqPointsMin,
            prereqPointsMax: o.prereqPointsMax,
          } : undefined,
        }),
      ),
    );
  };

  const persistCommunication = async (errorMessage: string): Promise<boolean> => {
    if (!canSaveDefinition) {
      setDraftError('Veuillez saisir un titre et sélectionner un canal avant d\'enregistrer.');
      return false;
    }
    setSavingDraft(true);
    setDraftError('');
    try {
      if (definition.channel === 'email') {
        const emailContent = await creationStepRef.current?.getEmailContent();
        if (emailContent) {
          creationPayloadRef.current = {
            ...(creationPayloadRef.current ?? {}),
            emailEditorMode: emailContent.mode,
            emailHtml: emailContent.html,
            ...(emailContent.design ? { emailDesign: emailContent.design } : {}),
          };
        }
      }
      const commId = await ensureCommunicationSaved();
      if (commId === null) {
        setDraftError(errorMessage);
        return false;
      }
      if (communicationInputId !== null) await patchAllData(commId);
      await saveOffersForComm(commId);
      return true;
    } finally {
      setSavingDraft(false);
    }
  };

  const handleTerminate = async () => {
    const ok = await persistCommunication('Erreur lors de l\'enregistrement de la communication.');
    if (!ok) return;
    resetDialog();
    onSaved?.();
    onClose();
  };

  const handleSaveDraft = async () => {
    const ok = await persistCommunication('Erreur lors de l\'enregistrement du brouillon.');
    if (!ok) return;
    resetDialog();
    onSaved?.();
    onClose();
  };

  return (
  <>
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={fullScreen}
      maxWidth="xl"
      fullWidth
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 2, minHeight: '90vh' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1, borderBottom: '1px solid #e9ecef' }}>
        <Typography variant="h6" fontWeight={600} sx={{ fontSize: '1rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {editingComm
            ? t('pages.marketing.communication.editionTitle')
            : t('pages.marketing.communication.editionTitle')}
        </Typography>
        <IconButton size="small" onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', overflow: 'hidden' }}>
        <Tabs
          orientation="vertical"
          value={activeStep}
          onChange={handleTabChange}
          sx={{
            width: 200,
            minWidth: 200,
            borderRight: '1px solid #dee2e6',
            backgroundColor: '#f8f9fa',
            '& .MuiTabs-indicator': { backgroundColor: sidebarColor, left: 0, right: 'auto', width: 3 },
            '& .MuiTab-root': {
              minHeight: 72,
              textTransform: 'none',
              alignItems: 'center',
              justifyContent: 'flex-start',
              flexDirection: 'row',
              gap: 1.5,
              px: 2.5,
              color: '#6c757d',
              fontWeight: 500,
              '&.Mui-selected': { color: sidebarColor, fontWeight: 700 },
              '&.Mui-disabled': { opacity: 0.4 },
            },
          }}
        >
          {STEPS.map(({ label, Icon }, idx) => (
            <Tab
              key={label}
              label={label}
              icon={
                <Icon
                  fontSize="small"
                  sx={{ color: activeStep === idx ? sidebarColor : '#adb5bd', mr: 0 }}
                />
              }
              iconPosition="start"
            />
          ))}
        </Tabs>

        <Box sx={{ flexGrow: 1, overflow: 'auto', minHeight: '60vh' }}>
          {saveError && <Alert severity="error" sx={{ m: 2 }}>{saveError}</Alert>}

          <Box sx={{ display: activeStep === 0 ? 'block' : 'none' }}>
            <CommunicationDefinitionStep data={definition} onChange={handleDefinitionChange} />
          </Box>
          <Box sx={{ display: activeStep === 1 ? 'block' : 'none' }}>
            {mountedSteps.has(1) && <CommunicationTargetingStep data={targeting} onChange={handleTargetingChange} />}
          </Box>
          <Box sx={{ display: activeStep === 2 ? 'block' : 'none' }}>
            {mountedSteps.has(2) && (
              <CommunicationCreationStep
                ref={creationStepRef}
                channel={definition.channel}
                onHasContent={setHasCreation}
                offerCount={localOffers.length}
                offerTitles={localOffers.map(o => o.title.trim())}
                onAddOffer={() => setLocalOffers(prev => [...prev, emptyOffer(String(Date.now()))])}
                onOfferUsed={idx => setUsedOfferIndices(prev => new Set([...prev, idx]))}
                onOfferIndicesChange={setUsedOfferIndices}
                initialPayload={editingComm?.creationPayload ?? null}
                onPayloadChange={payload => {
                  creationPayloadRef.current = payload;
                  if (payload.type === 'sms') {
                    const msg = (payload.message as string) || '';
                    const indices = new Set<number>();
                    let m: RegExpExecArray | null;
                    const re = /#NUM_COUPON_(\d+)#/g;
                    while ((m = re.exec(msg)) !== null) indices.add(parseInt(m[1]));
                    setUsedOfferIndices(indices);
                  }
                }}
              />
            )}
          </Box>
          <Box sx={{ display: activeStep === 3 ? 'block' : 'none' }}>
            {mountedSteps.has(3) && (
              <CommunicationOffersStep
                ref={offersRef}
                offers={localOffers}
                onOffersChange={offers => { setLocalOffers(offers); setIsDirty(true); }}
                usedOfferIndices={usedOfferIndices}
              />
            )}
          </Box>
          <Box sx={{ display: activeStep === 4 ? 'block' : 'none' }}>
            {mountedSteps.has(4) && (
              <CommunicationValidationStep
                definition={definition}
                targeting={targeting}
                offers={validationOffers}
                hasCreation={hasCreation}
                onBatAcknowledged={setBatAcknowledged}
              />
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid #e9ecef', px: 3, py: 1.5, flexDirection: 'column', alignItems: 'stretch', gap: 0 }}>
        {draftError && (
          <Alert severity="error" onClose={() => setDraftError('')} sx={{ mb: 1, py: 0.25 }}>
            {draftError}
          </Alert>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            {communicationInputId !== null && (
              <Typography variant="caption" color="text.secondary">
                Communication #{communicationInputId} {editingComm ? '(édition)' : 'créée'}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {activeStep > 0 && (
              <Button
                variant="text"
                size="small"
                onClick={() => setActiveStep(s => s - 1)}
                sx={{ color: '#6c757d', textTransform: 'none' }}
              >
                Précédent
              </Button>
            )}
            <Button
              variant="outlined"
              size="small"
              startIcon={savingDraft ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
              onClick={handleSaveDraft}
              disabled={savingDraft || saving}
              sx={{ color: '#6c757d', borderColor: '#dee2e6', textTransform: 'none', fontWeight: 500 }}
            >
              Enregistrer le brouillon
            </Button>
            {activeStep === STEPS.length - 1 ? (
              <Tooltip
                title={!batAcknowledged ? 'Veuillez cocher "J\'ai pris connaissance du BAT" avant de terminer' : ''}
                disableHoverListener={batAcknowledged}
              >
                <span>
                  <Button
                    variant="outlined"
                    startIcon={savingDraft ? <CircularProgress size={14} color="inherit" /> : undefined}
                    onClick={handleTerminate}
                    disabled={saving || savingDraft || !batAcknowledged}
                    sx={{ color: sidebarColor, borderColor: sidebarColor, textTransform: 'none', fontWeight: 600 }}
                  >
                    Terminer
                  </Button>
                </span>
              </Tooltip>
            ) : (
              <Button
                variant="contained"
                endIcon={saving ? <CircularProgress size={14} color="inherit" /> : <ArrowForwardIcon />}
                onClick={handleNext}
                disabled={saving || savingDraft}
                sx={{
                  backgroundColor: sidebarColor,
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': { backgroundColor: sidebarColor, opacity: 0.9 },
                }}
              >
                {t('pages.marketing.communication.nextStep')}
              </Button>
            )}
          </Box>
        </Box>
      </DialogActions>
    </Dialog>

    <DiscardChangesDialog
      open={confirmCloseOpen}
      onClose={() => setConfirmCloseOpen(false)}
      onSaveDraft={handleConfirmSaveDraft}
      onDiscard={handleConfirmDiscard}
      savingDraft={savingDraft}
    />
  </>
  );
}

export default CommunicationDialog;
